/**
 * Builds optimized context under 900 tokens
 */

import type { 
  RemyContext, 
  SessionMemory, 
  ChatMessage,
  UserPreferences 
} from '@/types/ai';
import type { FusedRecommendation } from '@/services/recommendations-fusion.service';

export function buildRemyContext(
  memory: SessionMemory,
  userPrefs?: UserPreferences,
  friendRecs?: FusedRecommendation[]
): RemyContext {
  const timeOfDay = getTimeOfDay();
  
  return {
    summary: memory.summary || generateQuickSummary(memory),
    recentMessages: getOptimizedMessages(memory.messages),
    userPrefs,
    savedVenues: memory.context.savedVenues.slice(-10), // Last 10 saved
    viewedVenues: memory.context.viewedVenues.slice(-20), // Last 20 viewed
    currentFilters: {
      pills: memory.context.selectedPills,
      neighborhoods: memory.context.neighborhoods,
      ...memory.context.currentFilters
    },
    friendRecommendations: friendRecs?.slice(0, 5), // Top 5 friend recs
    city: memory.context.city,
    neighborhood: memory.context.neighborhoods[0],
    timeOfDay,
    userContext: determineUserContext(memory),
    stage: determineStage(memory)
  };
}

function generateQuickSummary(memory: SessionMemory): string {
  const { messages, context } = memory;
  
  if (messages.length === 0) {
    return `New session in ${context.city}`;
  }
  
  const interests = context.selectedPills.join(', ');
  const venueCount = context.savedVenues.length;
  
  let summary = `User exploring ${context.city}`;
  if (interests) {
    summary += `, interested in ${interests}`;
  }
  if (venueCount > 0) {
    summary += `, ${venueCount} venues saved`;
  }
  
  return summary;
}

function getOptimizedMessages(messages: ChatMessage[]): ChatMessage[] {
  if (messages.length === 0) return [];
  
  const recent = messages.slice(-6);
  
  return recent.map(msg => ({
    ...msg,
    content: msg.content.length > 500 
      ? msg.content.substring(0, 497) + '...' 
      : msg.content
  }));
}

function getTimeOfDay(): 'morning' | 'afternoon' | 'evening' | 'night' {
  const hour = new Date().getHours();
  if (hour >= 6 && hour < 12) return 'morning';
  if (hour >= 12 && hour < 17) return 'afternoon';
  if (hour >= 17 && hour < 21) return 'evening';
  return 'night';
}

function determineUserContext(memory: SessionMemory): 'here_now' | 'planning_ahead' {
  const lastActivity = memory.context.lastActivity || 0;
  const timeSinceActivity = Date.now() - lastActivity;
  
  // If actively browsing (within last 5 minutes), assume here now
  if (timeSinceActivity < 5 * 60 * 1000) {
    return 'here_now';
  }
  
  return 'planning_ahead';
}

function determineStage(memory: SessionMemory): 'discover' | 'refine' | 'book' {
  const { savedVenues, viewedVenues } = memory.context;
  
  if (savedVenues.length >= 5) {
    return 'book'; // Ready to plan/book
  }
  
  if (viewedVenues.length >= 10 || savedVenues.length >= 2) {
    return 'refine'; // Narrowing down choices
  }
  
  return 'discover'; // Initial exploration
}

export function formatSystemMessage(context: RemyContext): string {
  const parts = [
    `You are Remy, an AI travel concierge for ${context.city}.`,
    `Current time: ${context.timeOfDay}.`,
    `User stage: ${context.stage}.`
  ];
  
  if (context.summary) {
    parts.push(`Context: ${context.summary}`);
  }
  
  if (context.userPrefs) {
    const { interests, pace, travelStyle } = context.userPrefs;
    if (interests.length > 0) {
      parts.push(`User interests: ${interests.join(', ')}`);
    }
    if (pace) {
      parts.push(`Travel pace: ${pace}`);
    }
    if (travelStyle) {
      parts.push(`Travel style: ${travelStyle}`);
    }
  }
  
  if (context.savedVenues.length > 0) {
    parts.push(`User has saved ${context.savedVenues.length} venues`);
  }
  
  if (context.currentFilters.pills?.length > 0) {
    parts.push(`Active filters: ${context.currentFilters.pills.join(', ')}`);
  }
  
  return parts.join(' ');
}

export function estimateTokenCount(context: RemyContext): number {
  const systemMessage = formatSystemMessage(context);
  const messagesText = context.recentMessages
    .map(m => m.content)
    .join(' ');
  
  const totalText = systemMessage + messagesText;
  
  // Rough estimate: 1 token per 4 characters
  return Math.ceil(totalText.length / 4);
}

export function optimizeContext(
  context: RemyContext, 
  maxTokens: number = 900
): RemyContext {
  const optimized = { ...context };
  let tokens = estimateTokenCount(optimized);
  
  while (tokens > maxTokens) {
    if (optimized.friendRecommendations && optimized.friendRecommendations.length > 2) {
      optimized.friendRecommendations = optimized.friendRecommendations.slice(0, 2);
    } else if (optimized.viewedVenues.length > 10) {
      optimized.viewedVenues = optimized.viewedVenues.slice(-10);
    } else if (optimized.recentMessages.length > 4) {
      optimized.recentMessages = optimized.recentMessages.slice(-4);
    } else {
      if (optimized.summary.length > 100) {
        optimized.summary = optimized.summary.substring(0, 97) + '...';
      }
      break;
    }
    
    tokens = estimateTokenCount(optimized);
  }
  
  return optimized;
}